/**
 * Delete, at the foot of a rule's editor.
 *
 * Deleting is reached by opening the rule, never from the drawer: a drawer is
 * for looking, and a destructive button beside Edit and Deploy is one slip
 * away from the wrong one. The press-and-hold is the Catalog's
 * (`.cat-card-delete`): let go early and nothing happens. When the hold
 * completes the strip fires `argus-delete`; the owner does the deleting and
 * says whether it stuck.
 */

import { html, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import type { ArgusRule } from '../../api/types.ts';

/** How long Delete must be held - the Catalog's number, so both feel the same. */
const DELETE_HOLD_MS = 750;

@customElement('argus-delete-strip')
export class ArgusDeleteStrip extends LightElement {
  @property({ attribute: false }) rule: ArgusRule | null = null;

  @state() private holding = false;
  private timer: number | undefined;

  override disconnectedCallback(): void {
    window.clearTimeout(this.timer);
    super.disconnectedCallback();
  }

  private start(e?: PointerEvent): void {
    if (this.holding || !this.rule) return;
    if (e) {
      try {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      } catch {
        // No capturable pointer: hold without it.
      }
    }
    const rule = this.rule;
    this.holding = true;
    this.timer = window.setTimeout(() => {
      this.holding = false;
      this.dispatchEvent(new CustomEvent('argus-delete', { detail: { rule }, bubbles: true, composed: true }));
    }, DELETE_HOLD_MS);
  }

  /** Let go early and nothing happens - that is the whole safety of it. */
  private cancel(): void {
    window.clearTimeout(this.timer);
    this.holding = false;
  }

  override render(): TemplateResult {
    const running = this.rule?.status === 'deployed';
    return html`
      <div class="argus-delete-strip">
        <p>
          ${running
            ? 'Deleting stops this rule first, then removes it and its versions from every screen.'
            : 'Deleting removes this rule and its versions from every screen.'}
          Press and hold to confirm.
        </p>
        <button
          type="button"
          class="dash-btn cat-card-delete argus-hold-delete ${this.holding ? 'is-holding' : ''}"
          style=${`--hold-ms: ${DELETE_HOLD_MS}ms`}
          title=${running ? 'Press and hold to stop and delete' : 'Press and hold to delete'}
          @pointerdown=${(e: PointerEvent) => {
            if (e.button === 0) this.start(e);
          }}
          @pointerup=${() => this.cancel()}
          @pointerleave=${() => this.cancel()}
          @pointercancel=${() => this.cancel()}
          @keydown=${(e: KeyboardEvent) => {
            if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
              e.preventDefault();
              this.start();
            }
          }}
          @keyup=${() => this.cancel()}
        >
          <span class="cat-card-delete-fill"></span>
          <span class="cat-card-delete-label">Delete rule <span class="argus-hold-word">hold</span></span>
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'argus-delete-strip': ArgusDeleteStrip;
  }
}
